import { useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import { useNavigate, useParams } from "react-router-dom";
import { ScrollArea } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { BsArrowLeft } from "react-icons/bs";
import { BiDownload } from "react-icons/bi";
import { ChartGantt, ReceiptText, Ticket, WalletCards } from "lucide-react";
// @ts-ignore
import html2pdf from "html2pdf.js";

import { IAirportDetails } from "types";
import { ECard } from "libs/ui/src/Card/ECard";
import { useApi, useFlightData } from "libs/data-access";
import FareSummary from "libs/ui/src/FareSummary/FareSummary";
import { IFlightOnlineBookingList } from "libs/data-access/types";
import {
  EButton,
  ECheckbox,
  ELoadingOverlay,
  EModal,
  ETable,
  ETooltip,
} from "libs/ui";
import {
  checkIfStatusMatchesForFlightBookings,
  getUserRole,
  showToast,
} from "utils/utility";

import FlightDetails from "./FlightDetails";
import PassportInfo from "./PassportInfo";
import ResolveTicket from "./FlightTicketActions/ResolveTicket";
import JourneyDetails from "./JourneyDetails/JourneyDetails";
import MarkAsToBeRefunded from "./FlightTicketActions/MarkAsToBeRefunded";

import "./FlightBookingDetails1G.scss";

import ETicketModern from "./ETicket/ETicketModern";
import FlightRefundAdvice from "./Invoice/FlightRefundAdvice";
import FlightPaymentAdvice from "./Invoice/FlightPaymentAdvice";
import Cancel1GBooking from "./FlightTicketActions/Cancel1GBooking";
import FlightRefundTicket from "./ManageRefund/FlightRefundTicket";
import FlightTicketTimeline from "./Timeline/FlightTicketTimeline";
import RefundStatusTimeline from "./FlightTicketActions/RefundStatusTimeline";
import EditFlightRefundCharges from "./FlightTicketActions/EditFlightRefundCharges";

import { manageFlightCharges } from "../hooks/utility";
import ExternalAPILog from "./ExternalAPILog/ExternalAPILog";
import { initialFlightTicket } from "initialStates";

export default function FlightBookingDetails1G() {
  const { uuid } = useParams();
  const navigate = useNavigate();

  const { getFlightData } = useFlightData();
  const { postData } = useApi();

  const ticketRef: any = useRef(null);
  const ticketWithoutFareRef: any = useRef(null);
  const paymentAdviceRef: any = useRef(null);
  const refundAdviceRef: any = useRef(null);

  const [showTicket, { open: openTicket, close: closeTicket }] =
    useDisclosure(false);
  const [
    showTicketWithoutFare,
    { open: openTicketWithoutFare, close: closeTicketWithoutFare },
  ] = useDisclosure(false);
  const [
    showPaymentAdvice,
    { open: openPaymentAdvice, close: closePaymentAdvice },
  ] = useDisclosure(false);
  const [
    showRefundAdvice,
    { open: openRefundAdvice, close: closeRefundAdvice },
  ] = useDisclosure(false);
  const [showTimeline, { open: openTimeline, close: closeTimeline }] =
    useDisclosure(false);

  const [flightTicket, setFlightTicket] =
    useState<IFlightOnlineBookingList>(initialFlightTicket);
  const [airportDetails, setAirportDetails] = useState<IAirportDetails[]>([]);
  const [isLoadingFlightTicket, setIsLoadingFlightTicket] =
    useState<boolean>(false);
  const [showTicketWithFare, setShowTicketWithFare] = useState<boolean>(false);

  useEffect(() => {
    uuid && getFlightDetails();
  }, [uuid]);

  useEffect(() => {
    if (flightTicket.uuid) {
      fetchAirportInformation();
    }
  }, [flightTicket.uuid]);

  const isInternational = useMemo(() => {
    if (!airportDetails || airportDetails.length === 0) return false;

    // Check if any airport has a country ID other than India (179)
    return airportDetails.some((airport) => airport.countryId !== 179);
  }, [airportDetails]);

  // HANDLER FUNCTIONS
  const handlePrintTicket = async () => {
    var element = ticketRef.current;
    await document.fonts.ready;

    html2pdf()
      .set({
        // margin: [15, 15, 15, 15],
        filename: `e-Ticket_${flightTicket?.bookingResponses?.[0]?.bookingDate || "--"}.pdf`,
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: {
          scale: window.devicePixelRatio * 2, //2 previous
          letterRendering: true,
          useCORS: true,
          logging: true,
        },
        jsPDF: {
          unit: "mm",
          format: "a4",
          orientation: "portrait",
          compress: false,
          precision: 16,
        },
        pagebreak: {
          mode: ["avoid-all", "css", "legacy"],
          before: ".page-break-before",
          after: ".page-break-after",
          avoid: ["tr", "td", ".no-break"],
        },
      })
      .from(element)
      .save();
  };

  const handlePrintTicketInvoice = () => {
    var element = paymentAdviceRef.current;

    html2pdf()
      .set({ top: 2 })
      .from(element)
      .save(
        `e-Payment_Advice_${flightTicket?.bookingResponses?.[0]?.bookingDate || "--"}.pdf`,
      );
  };

  const handlePrintRefundAdvice = () => {
    var element = refundAdviceRef.current;

    html2pdf()
      .set({ top: 2 })
      .from(element)
      .save(
        `e-Refund_Advice_${flightTicket?.bookingResponses?.[0]?.bookingDate || "--"}.pdf`,
      );
  };

  // API CALLS
  const getFlightDetails = async () => {
    setIsLoadingFlightTicket(true);

    const response = await getFlightData(`/booking/${uuid}`);

    if (response.isSuccess) {
      // console.log(
      //   "Response for getting flightTicket details: ",
      //   response.data?.data,
      // );
      setFlightTicket(response.data?.data);
    } else {
      console.log("Error while getting flightTicket details: ", response);
      showToast({
        type: "error",
        message:
          response.message || "Error while getting flightTicket details!",
      });
    }

    setIsLoadingFlightTicket(false);
  };

  const fetchAirportInformation = async () => {
    let allAirportCode: any[] = [];
    flightTicket?.bookingResponses?.forEach((trip) => {
      // navitaire
      trip?.navitaireBookingDetailResponses?.forEach((journey) => {
        journey?.navitaireBookingDetailSegments?.forEach((segment) => {
          allAirportCode = [
            ...allAirportCode,
            segment.origin,
            segment.destination,
          ];
        });
      });
      // galileo
      trip?.galileoBookingDetailResponses?.forEach((journey) => {
        journey?.flightBookingSegments?.forEach((segment) => {
          allAirportCode = [
            ...allAirportCode,
            segment.origin,
            segment.destination,
          ];
        });
      });
      // akbar
      trip?.akbarBookingDetailResponses?.forEach((trip) => {
        trip?.akbarBookingDetailJourneys?.forEach((journey) => {
          allAirportCode = [
            ...allAirportCode,
            journey.from,
            journey.to,
            ...journey?.connections?.map((segment) => segment?.Airport),
          ];
        });
      });
    });

    const response = await postData(
      `unsecure/flight/one-g/get-airport-response?codes=${allAirportCode}`,
      null,
    );
    if (response.isSuccess) {
      setAirportDetails(response.data.data);
    } else {
      console.error(
        "Error occurred while requesting for Air Pricing:",
        response,
      );
    }
  };

  return (
    <>
      {isLoadingFlightTicket ? (
        <ELoadingOverlay />
      ) : (
        <div className="mt-2">
          {/* TOP CONTROLS */}
          <div className="flex flex-col md:flex-row justify-between gap-4 mb-6 p-2 bg-gray-50 rounded-lg shadow-sm">
            <EButton
              size="sm"
              variant="outline"
              leftSection={<BsArrowLeft />}
              onClick={() => navigate(-1)}
              className="self-start"
            >
              Back to Bookings
            </EButton>

            <div className="flex flex-wrap gap-2">
              <div className="flex gap-2">
                <ETooltip label="Download e-Ticket">
                  <EButton
                    size="sm"
                    variant="light"
                    color="blue"
                    onClick={openTicket}
                  >
                    <Ticket size={20} className="mr-1" />
                    <span className="hidden sm:inline">Ticket</span>
                  </EButton>
                </ETooltip>
              </div>

              <div className="flex gap-2">
                {checkIfStatusMatchesForFlightBookings("some", flightTicket, [
                  "SUCCESS",
                ]) ? (
                  <ETooltip label="Download e-Payment Advice">
                    <EButton
                      size="sm"
                      variant="light"
                      color="blue"
                      onClick={openPaymentAdvice}
                    >
                      <ReceiptText size={20} className="mr-1" />
                      <span className="hidden sm:inline">Payment Advice</span>
                    </EButton>
                  </ETooltip>
                ) : (
                  <></>
                )}

                {checkIfStatusMatchesForFlightBookings("some", flightTicket, [
                  "REFUNDED",
                  "TO_BE_REFUNDED",
                ]) ? (
                  <ETooltip label="Download e-Refund Advice">
                    <EButton
                      size="sm"
                      variant="light"
                      color="blue"
                      onClick={openRefundAdvice}
                    >
                      <WalletCards size={20} className="mr-1" />
                      <span className="hidden sm:inline">Refund Advice</span>
                    </EButton>
                  </ETooltip>
                ) : (
                  <></>
                )}
              </div>

              <div className="flex gap-2">
                <ETooltip label="View Timeline">
                  <EButton
                    size="sm"
                    variant="light"
                    color="blue"
                    onClick={openTimeline}
                  >
                    <ChartGantt size={20} className="mr-1" />
                    <span className="hidden sm:inline">Timeline</span>
                  </EButton>
                </ETooltip>

                {checkIfStatusMatchesForFlightBookings("some", flightTicket, [
                  "TO_BE_REFUNDED",
                ]) && getUserRole().includes("ADMIN") ? (
                  <ETooltip label="Edit Refund Charges">
                    <EditFlightRefundCharges
                      flightTicket={flightTicket}
                      handleRefresh={() => getFlightDetails()}
                    />
                  </ETooltip>
                ) : (
                  <></>
                )}
              </div>

              <ExternalAPILog traceId={flightTicket?.traceId} />

              {getUserRole().includes("ADMIN") ? (
                <div className="flex gap-2">
                  <MarkAsToBeRefunded
                    label={"Reject"}
                    flightTicket={flightTicket}
                    handleRefresh={() => getFlightDetails()}
                  />

                  <ResolveTicket
                    flightTicket={flightTicket}
                    handleRefresh={() => getFlightDetails()}
                  />
                </div>
              ) : (
                <></>
              )}

              {checkIfStatusMatchesForFlightBookings("some", flightTicket, [
                "SUCCESS",
              ]) && getUserRole() ? (
                <Cancel1GBooking
                  flightTicket={flightTicket}
                  handleRefresh={() => getFlightDetails()}
                />
              ) : (
                <></>
              )}

              {getUserRole().includes("ADMIN") ? (
                <FlightRefundTicket
                  flightTicket={flightTicket}
                  handleRefresh={() => getFlightDetails()}
                />
              ) : (
                <></>
              )}

              {checkIfStatusMatchesForFlightBookings("some", flightTicket, [
                "TO_BE_REFUNDED",
                "REFUNDED",
              ]) ? (
                <RefundStatusTimeline flightTicket={flightTicket} />
              ) : (
                <></>
              )}
            </div>
          </div>

          {/* FLIGHT DETAILS & PASSENGER DETAILS */}
          <div className="grid grid-cols-12 gap-6">
            <div className="cols-span-12 sm:col-span-8">
              <ECard title="Flight Details">
                {/* FLIGHT DETAILS */}
                <FlightDetails
                  flightTicket={flightTicket}
                  airportDetails={airportDetails}
                  areFareRulesToBeVisible={true}
                />
              </ECard>

              {/* CONTACT PERSON */}
              <div className="mt-6">
                <ECard title="Contact Person">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <div className="text-sm text-gray-500">Email</div>
                      <div className="font-medium">
                        {flightTicket?.email || "Not provided"}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-500">
                        Contact Number
                      </div>
                      <div className="font-medium">
                        {flightTicket?.contactNumber || "Not provided"}
                      </div>
                    </div>
                  </div>
                </ECard>
              </div>

              {/* PASSENGER DETAILS */}
              <div className="mt-6">
                <ECard title="Passenger Details">
                  <ETable>
                    <thead>
                      <tr>
                        <th>S.No.</th>
                        <th>Passenger</th>
                        <th>D.O.B.</th>
                        <th>Passport Info.</th>
                      </tr>
                    </thead>
                    <tbody>
                      {flightTicket?.bookingResponses?.[0]
                        ?.galileoPassengerDetailResponses ? ( // for galileo
                        flightTicket?.bookingResponses?.[0]?.galileoPassengerDetailResponses?.map(
                          (eachPassenger, index) => (
                            <tr key={`passenger-${index}`}>
                              <td>{index + 1}</td>
                              <td className="font-medium">
                                <span>
                                  {eachPassenger?.title}{" "}
                                  {eachPassenger?.firstName}{" "}
                                  {eachPassenger?.lastName}
                                </span>
                                <span> ({eachPassenger?.gender})</span>
                              </td>
                              <td>
                                {eachPassenger?.dateOfBirth
                                  ? moment(eachPassenger?.dateOfBirth).format(
                                      "DD-MM-YYYY",
                                    )
                                  : ""}
                              </td>
                              <td>
                                <PassportInfo
                                  passportNumber={eachPassenger?.passportNumber}
                                  passportIssueDate={
                                    eachPassenger?.passportIssueDate
                                  }
                                  passportExpiryDate={
                                    eachPassenger?.passportExpiryDate
                                  }
                                />
                              </td>
                            </tr>
                          ),
                        )
                      ) : flightTicket?.bookingResponses?.[0]
                          ?.navitairePassengerDetailResponses ? ( // for navitaire
                        flightTicket?.bookingResponses?.[0]?.navitairePassengerDetailResponses?.map(
                          (eachPassenger, index) => (
                            <>
                              <tr key={`passenger-${index}`}>
                                <td>{index + 1}</td>
                                <td className="font-medium">
                                  <span>
                                    {eachPassenger?.title}{" "}
                                    {eachPassenger?.firstName}{" "}
                                    {eachPassenger?.lastName}
                                  </span>
                                  <span> ({eachPassenger?.gender})</span>
                                </td>
                                <td>
                                  {eachPassenger?.dateOfBirth
                                    ? moment(eachPassenger?.dateOfBirth).format(
                                        "DD-MM-YYYY",
                                      )
                                    : ""}
                                </td>
                                <td>
                                  <PassportInfo
                                    passportNumber={
                                      eachPassenger?.passportNumber
                                    }
                                    passportIssueDate={
                                      eachPassenger?.passportIssueDate
                                    }
                                    passportExpiryDate={
                                      eachPassenger?.passportExpiryDate
                                    }
                                  />
                                </td>
                              </tr>
                              {/* INFANT DETAILS */}
                              {eachPassenger?.infantDetail && (
                                <tr
                                  key={`passenger-${index}-infant`}
                                  className="bg-blue-50 text-sm"
                                >
                                  <td></td>
                                  <td className="font-medium">
                                    <span className="text-xs font-semibold text-blue-600 bg-blue-100 px-1.5 py-0.5 rounded mr-1">
                                      Infant
                                    </span>
                                    <span>
                                      {eachPassenger.infantDetail?.title}{" "}
                                      {eachPassenger.infantDetail?.firstName}{" "}
                                      {eachPassenger.infantDetail?.lastName}
                                    </span>
                                    <span>
                                      {" "}
                                      ({eachPassenger.infantDetail?.gender})
                                    </span>
                                  </td>
                                  <td>
                                    {eachPassenger.infantDetail?.dateOfBirth
                                      ? moment(
                                          eachPassenger.infantDetail
                                            ?.dateOfBirth,
                                        ).format("DD-MM-YYYY")
                                      : ""}
                                  </td>
                                  <td>
                                    <PassportInfo
                                      passportNumber={
                                        eachPassenger.infantDetail
                                          ?.passportNumber
                                      }
                                      passportIssueDate={
                                        eachPassenger.infantDetail
                                          ?.passportIssueDate
                                      }
                                      passportExpiryDate={
                                        eachPassenger.infantDetail
                                          ?.passportExpiryDate
                                      }
                                    />
                                  </td>
                                </tr>
                              )}
                            </>
                          ),
                        )
                      ) : flightTicket?.bookingResponses?.[0]
                          ?.akbarPassengerDetailResponses ? ( // for akbar
                        flightTicket?.bookingResponses?.[0]?.akbarPassengerDetailResponses?.map(
                          (eachPassenger, index) => (
                            <>
                              <tr key={`passenger-${index}`}>
                                <td>{index + 1}</td>
                                <td className="font-medium">
                                  <span>
                                    {eachPassenger?.title}{" "}
                                    {eachPassenger?.firstName}{" "}
                                    {eachPassenger?.lastName}
                                  </span>
                                  <span> ({eachPassenger?.gender})</span>
                                </td>
                                <td>
                                  {eachPassenger?.dateOfBirth
                                    ? moment(eachPassenger?.dateOfBirth).format(
                                        "DD-MM-YYYY",
                                      )
                                    : ""}
                                </td>
                                <td>
                                  <PassportInfo
                                    passportNumber={
                                      eachPassenger?.passportNumber
                                    }
                                    passportIssueDate={
                                      eachPassenger?.passportIssueDate
                                    }
                                    passportExpiryDate={
                                      eachPassenger?.passportExpiryDate
                                    }
                                  />
                                </td>
                              </tr>
                              {/* INFANT DETAILS */}
                              {eachPassenger?.infantDetail && (
                                <tr
                                  key={`passenger-${index}-infant`}
                                  className="bg-blue-50 text-sm"
                                >
                                  <td></td>
                                  <td className="font-medium">
                                    <span className="text-xs font-semibold text-blue-600 bg-blue-100 px-1.5 py-0.5 rounded mr-1">
                                      Infant
                                    </span>
                                    <span>
                                      {eachPassenger.infantDetail?.title}{" "}
                                      {eachPassenger.infantDetail?.firstName}{" "}
                                      {eachPassenger.infantDetail?.lastName}
                                    </span>
                                    <span>
                                      {" "}
                                      ({eachPassenger.infantDetail?.gender})
                                    </span>
                                  </td>
                                  <td>
                                    {eachPassenger.infantDetail?.dateOfBirth
                                      ? moment(
                                          eachPassenger.infantDetail
                                            ?.dateOfBirth,
                                        ).format("DD-MM-YYYY")
                                      : ""}
                                  </td>
                                  <td>
                                    <PassportInfo
                                      passportNumber={
                                        eachPassenger.infantDetail
                                          ?.passportNumber
                                      }
                                      passportIssueDate={
                                        eachPassenger.infantDetail
                                          ?.passportIssueDate
                                      }
                                      passportExpiryDate={
                                        eachPassenger.infantDetail
                                          ?.passportExpiryDate
                                      }
                                    />
                                  </td>
                                </tr>
                              )}
                            </>
                          ),
                        )
                      ) : (
                        <></>
                      )}
                    </tbody>
                  </ETable>
                </ECard>
              </div>
            </div>

            <div className="col-span-12 md:col-span-4">
              <ECard title="Fare Summary">
                {flightTicket.uuid ? (
                  <FareSummary
                    service="FLIGHT"
                    refundCharges={manageFlightCharges(flightTicket)}
                    airPriceJourneyResponseList={flightTicket?.paymentOrder}
                  />
                ) : (
                  <></>
                )}
              </ECard>
            </div>
          </div>
        </div>
      )}

      {/* TICKET */}
      <EModal
        size="80%"
        scrollAreaComponent={ScrollArea.Autosize}
        opened={showTicket}
        onClose={() => {
          closeTicket();
        }}
        title={
          <div className="flex items-center gap-2">
            <EButton
              variant="subtle"
              rightSection={<BiDownload />}
              onClick={handlePrintTicket}
            >
              Download
            </EButton>
            <ECheckbox
              size="xs"
              label="With Fare"
              checked={showTicketWithFare}
              onChange={(e) => setShowTicketWithFare(e.target.checked)}
            />
          </div>
        }
      >
        <div ref={ticketRef}>
          {/* <ETicket flightTicket={flightTicket} withFare={showTicketWithFare} /> */}
          <ETicketModern
            flightTicket={flightTicket}
            airportDetails={airportDetails}
            isInternational={isInternational}
            withFare={showTicketWithFare}
          />
        </div>
      </EModal>

      {/* PAYMENT ADVICE (Invoice) */}
      <EModal
        size="80%"
        scrollAreaComponent={ScrollArea.Autosize}
        opened={showPaymentAdvice}
        onClose={() => {
          closePaymentAdvice();
        }}
        title={
          <div className="flex justify-between items-center">
            <EButton
              variant="subtle"
              rightSection={<BiDownload />}
              onClick={handlePrintTicketInvoice}
            >
              Download
            </EButton>
          </div>
        }
      >
        <div ref={paymentAdviceRef}>
          <FlightPaymentAdvice flightTicket={flightTicket} />
        </div>
      </EModal>

      {/* REFUND ADVICE (Credit Note) */}
      <EModal
        size="80%"
        scrollAreaComponent={ScrollArea.Autosize}
        opened={showRefundAdvice}
        onClose={() => {
          closeRefundAdvice();
        }}
        title={
          <div className="flex justify-between items-center">
            <EButton
              variant="subtle"
              rightSection={<BiDownload />}
              onClick={handlePrintRefundAdvice}
            >
              Download
            </EButton>
          </div>
        }
      >
        <div ref={refundAdviceRef}>
          <FlightRefundAdvice flightTicket={flightTicket} />
        </div>
      </EModal>

      {/* TICKET TIMELINE */}
      <FlightTicketTimeline
        flightTicket={flightTicket}
        opened={showTimeline}
        onClose={() => closeTimeline()}
      />
    </>
  );
}
